<template>
  <section class="social-page">

    <!-- =====================================================
         HEADER
    ====================================================== -->

    <div class="social-card social-header-card">

      <div class="card-glow card-glow-blue"></div>
      <div class="card-glow card-glow-purple"></div>

      <div class="header-content">

        <div class="header-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="8"
              stroke="currentColor"
              stroke-width="1.7"
            />

            <path
              d="M4.8 9.5H19.2"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />

            <path
              d="M4.8 14.5H19.2"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />

            <path
              d="M12 4C14 6.2 15 8.85 15 12C15 15.15 14 17.8 12 20"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />

            <path
              d="M12 4C10 6.2 9 8.85 9 12C9 15.15 10 17.8 12 20"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
        </div>


        <div class="header-copy">

          <div class="eyebrow">
            Ваше присутствие
          </div>

          <h2 class="card-title">
            Социальные сети
          </h2>

          <p class="card-description">
            Добавьте сайт и социальные профили, чтобы другим
            пользователям было проще узнать вас вне Hr3.
          </p>

        </div>

      </div>

    </div>


    <!-- =====================================================
         SOCIAL LINKS
    ====================================================== -->

    <div class="social-card links-card">

      <div class="section-heading">

        <div class="section-eyebrow">
          Контакты и профили
        </div>

        <h3>
          Где вас можно найти
        </h3>

        <p>
          Укажите только те профили, которыми хотите делиться публично.
        </p>

      </div>


      <div
        class="social-list"
        :class="{
          'social-list--saving': isSaving
        }"
      >

        <div
          v-for="item in socialItems"
          :key="item.model"
          class="social-row"
          :class="{
            filled: !!normalizeValue(form[item.model]),
            focused: focusedField === item.model
          }"
        >

          <div class="platform-icon">
            <img
              :src="item.icon"
              :alt="item.label"
            />
          </div>


          <div class="platform-content">

            <div class="platform-title-row">

              <div class="platform-title">
                {{ item.label }}
              </div>

              <div
                class="status-badge"
                :class="{
                  active: !!normalizeValue(form[item.model])
                }"
              >
                <span class="status-dot"></span>

                {{
                  normalizeValue(form[item.model])
                    ? "Добавлено"
                    : "Не добавлено"
                }}
              </div>

            </div>


            <div class="platform-description">
              {{ item.description }}
            </div>


            <div class="input-shell">

              <input
                :id="`${item.model}-input`"
                v-model="form[item.model]"
                type="text"
                :placeholder="item.placeholder"
                :disabled="isSaving"
                autocomplete="off"
                spellcheck="false"
                @focus="focusedField = item.model"
                @blur="focusedField = null"
                @keydown.enter.prevent="saveChanges"
              />


              <div
                v-if="form[item.model]"
                class="connected-mark"
                aria-hidden="true"
              >
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 10.2L8.1 13.3L15 6.5"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

            </div>

          </div>

        </div>

      </div>


      <!-- =====================================================
           SAVE
      ====================================================== -->

      <div class="save-area">

        <div class="save-state">
          <span
            class="save-state-dot"
            :class="{ changed: isChanged }"
          ></span>

          {{
            isChanged
              ? "Есть несохранённые изменения"
              : "Все изменения сохранены"
          }}
        </div>


        <button
          class="save-btn"
          :class="{ active: isChanged && !isSaving }"
          :disabled="!isChanged || isSaving"
          type="button"
          @click="saveChanges"
        >
          <template v-if="isSaving">
            <span class="button-spinner"></span>
            <span>Сохраняем…</span>
          </template>

          <template v-else>
            Сохранить
          </template>
        </button>

      </div>

    </div>

  </section>
</template>


<script setup>

import {
  computed,
  ref,
  watch
} from "vue";

import SummaryService
  from "../../api/SummaryService";

import globeIcon
  from "../../assets/globe.png";

import facebookIcon
  from "../../assets/facebook-profile.png";

import instagramIcon
  from "../../assets/instagram-profile.png";

import linkedinIcon
  from "../../assets/linkedin-profile.png";

import xIcon
  from "../../assets/x-profile.png";

import telegramIcon
  from "../../assets/telegram-profile.png";

import youtubeIcon
  from "../../assets/youtube-profile.png";


const props = defineProps({

  initialSocial: {
    type: Object,
    required: true
  },

  snackbar: {
    type: Function,
    default: null
  }

});


const emit = defineEmits([
  "update"
]);


const isSaving =
  ref(false);

const focusedField =
  ref(null);


const socialItems = [

  {
    label: "Personal Website",
    model: "website",
    icon: globeIcon,
    description:
      "Ваш личный сайт, портфолио или персональная страница",
    placeholder:
      "https://your-site.com"
  },

  {
    label: "Facebook",
    model: "facebook",
    icon: facebookIcon,
    description:
      "Ваш профиль или публичная страница в Facebook",
    placeholder:
      "facebook.com/username"
  },

  {
    label: "Instagram",
    model: "instagram",
    icon: instagramIcon,
    description:
      "Личный или профессиональный Instagram-профиль",
    placeholder:
      "instagram.com/username"
  },

  {
    label: "LinkedIn",
    model: "linkedin",
    icon: linkedinIcon,
    description:
      "Профессиональный профиль и деловые контакты",
    placeholder:
      "linkedin.com/in/username"
  },

  {
    label: "X.com",
    model: "x",
    icon: xIcon,
    description:
      "Публичный профиль в X",
    placeholder:
      "x.com/username"
  },

  {
    label: "Telegram",
    model: "telegram",
    icon: telegramIcon,
    description:
      "Ваш Telegram-профиль или публичный канал",
    placeholder:
      "t.me/username"
  },

  {
    label: "YouTube",
    model: "youtube",
    icon: youtubeIcon,
    description:
      "Канал с видео, выступлениями или профессиональным контентом",
    placeholder:
      "youtube.com/@username"
  }

];


const form = ref(
  createFormFromInitial(
    props.initialSocial
  )
);


const initialForm = ref(
  JSON.stringify(
    normalizeForm(form.value)
  )
);


/* ============================================================
   CHANGES
============================================================ */

const isChanged = computed(() => {

  return (
    JSON.stringify(
      normalizeForm(form.value)
    )
    !==
    initialForm.value
  );

});


/* ============================================================
   WATCH
============================================================ */

watch(
  () => props.initialSocial,

  value => {

    if (isSaving.value) {
      return;
    }


    form.value =
      createFormFromInitial(
        value
      );


    initialForm.value =
      JSON.stringify(
        normalizeForm(
          form.value
        )
      );

  },

  {
    deep: true
  }
);


/* ============================================================
   FORM
============================================================ */

function createFormFromInitial(
  initialSocial = {}
) {

  return {

    website:
      initialSocial.website
      || initialSocial.websiteUrl
      || "",

    facebook:
      initialSocial.facebookId
      || "",

    instagram:
      initialSocial.instagramId
      || "",

    linkedin:
      initialSocial.linkedinId
      || "",

    telegram:
      initialSocial.telegramId
      || "",

    youtube:
      initialSocial.youtubeId
      || "",

    x:
      initialSocial.twitterId
      || ""

  };

}


function normalizeValue(value) {

  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();

}


function normalizeForm(value) {

  return {

    website:
      normalizeValue(value.website),

    facebook:
      normalizeValue(value.facebook),

    instagram:
      normalizeValue(value.instagram),

    linkedin:
      normalizeValue(value.linkedin),

    telegram:
      normalizeValue(value.telegram),

    youtube:
      normalizeValue(value.youtube),

    x:
      normalizeValue(value.x)

  };

}


/* ============================================================
   SNACKBAR
============================================================ */

function showSnackbar(
  message,
  type = "success"
) {

  if (!props.snackbar) {
    return;
  }


  props.snackbar(
    message,
    type
  );

}


/* ============================================================
   SAVE
============================================================ */

async function saveChanges() {

  if (
    !isChanged.value
    || isSaving.value
  ) {
    return;
  }


  const userId =
    localStorage.getItem(
      "user_id"
    );


  if (!userId) {

    showSnackbar(
      "Не удалось определить пользователя",
      "error"
    );

    return;
  }


  const normalized =
    normalizeForm(
      form.value
    );


  const payload = {

    website:
      normalized.website,

    facebookId:
      normalized.facebook,

    instagramId:
      normalized.instagram,

    linkedinId:
      normalized.linkedin,

    telegramId:
      normalized.telegram,

    youtubeId:
      normalized.youtube,

    twitterId:
      normalized.x

  };


  isSaving.value =
    true;


  try {

    const response =
      await SummaryService.updateProfileSocial(
        userId,
        payload
      );


    form.value = {
      ...form.value,
      ...normalized
    };


    initialForm.value =
      JSON.stringify(
        normalizeForm(
          form.value
        )
      );


    emit(
      "update",
      {
        ...payload,
        response
      }
    );


    showSnackbar(
      "Социальные сети успешно обновлены",
      "success"
    );


    console.log(
      "✅ Социальные сети обновлены:",
      payload
    );


  } catch (error) {

    console.error(
      "❌ Ошибка при обновлении соцсетей:",
      error
    );


    const message =
      error?.response?.data?.message
      || error?.message
      || "Не удалось сохранить изменения";


    showSnackbar(
      message,
      "error"
    );


  } finally {

    isSaving.value =
      false;

  }

}

</script>


<style scoped>

/* ============================================================
   ROOT
============================================================ */

.social-page {
  width: 100%;

  display: flex;
  flex-direction: column;

  gap: 16px;

  font-family:
    "Eastman-Regular",
    sans-serif;
}


/* ============================================================
   BASE CARD
============================================================ */

.social-card {
  position: relative;

  width: 100%;

  box-sizing: border-box;

  overflow: hidden;

  background: #ffffff;

  border:
    1px solid
    rgba(
      20,
      35,
      60,
      0.09
    );

  border-radius: 24px;

  box-shadow:
    0 16px 45px
    rgba(
      15,
      23,
      42,
      0.055
    );
}


/* ============================================================
   HEADER CARD
============================================================ */

.social-header-card {
  padding: 28px;
}


.header-content {
  position: relative;

  z-index: 2;

  display: flex;
  align-items: flex-start;

  gap: 15px;
}


.header-icon {
  width: 48px;
  height: 48px;

  flex:
    0
    0
    48px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #407edc;

  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, 0.12),
      rgba(124, 92, 231, 0.075)
    );

  border:
    1px solid
    rgba(
      40,
      120,
      230,
      0.11
    );

  border-radius: 15px;
}


.header-icon svg {
  width: 23px;
  height: 23px;
}


.header-copy {
  min-width: 0;
}


.eyebrow,
.section-eyebrow {
  margin-bottom: 5px;

  color: #9499a2;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  line-height: 1.2;

  letter-spacing: 0.07em;

  text-transform: uppercase;
}


.card-title {
  margin: 0;

  color: #292e35;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 17px;

  line-height: 1.3;

  letter-spacing: -0.25px;
}


.card-description {
  max-width: 560px;

  margin:
    6px
    0
    0;

  color: #80858e;

  font-size: 11px;

  line-height: 1.5;
}


/* ============================================================
   GLOW
============================================================ */

.card-glow {
  position: absolute;

  width: 220px;
  height: 220px;

  pointer-events: none;

  border-radius: 50%;

  filter:
    blur(75px);

  opacity: 0.1;
}


.card-glow-blue {
  top: -120px;
  right: -60px;

  background: #2887f7;
}


.card-glow-purple {
  bottom: -160px;
  left: -90px;

  background: #7c5ce7;
}


/* ============================================================
   LINKS CARD
============================================================ */

.links-card {
  padding:
    26px
    20px
    20px;
}


.section-heading {
  padding:
    0
    8px
    18px;
}


.section-heading h3 {
  margin: 0;

  color: #30353d;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 14px;

  font-weight: 500;
}


.section-heading p {
  margin:
    5px
    0
    0;

  color: #989da5;

  font-size: 9px;

  line-height: 1.4;
}


/* ============================================================
   SOCIAL LIST
============================================================ */

.social-list {
  display: flex;
  flex-direction: column;

  transition:
    opacity
    0.2s ease;
}


.social-list--saving {
  opacity: 0.55;

  pointer-events: none;
}


.social-row {
  display: grid;

  grid-template-columns:
    44px
    minmax(0, 1fr);

  gap: 13px;

  padding:
    15px
    10px;

  border-radius: 15px;

  transition:
    background-color
    0.2s ease;
}


.social-row + .social-row {
  border-top:
    1px solid
    rgba(
      25,
      40,
      60,
      0.06
    );

  border-radius: 0;
}


.social-row:hover,
.social-row.focused {
  background:
    rgba(
      40,
      135,
      247,
      0.032
    );
}


/* ============================================================
   PLATFORM ICON
============================================================ */

.platform-icon {
  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    #f5f7fa;

  border:
    1px solid
    rgba(
      30,
      45,
      65,
      0.055
    );

  border-radius: 13px;
}


.platform-icon img {
  width: 21px;
  height: 21px;

  display: block;

  object-fit: contain;

  opacity: 0.55;

  transition:
    opacity
    0.2s ease,
    transform
    0.2s ease;
}


.social-row.filled
.platform-icon img,
.social-row.focused
.platform-icon img {
  opacity: 1;
}


.social-row:hover
.platform-icon img {
  transform:
    scale(1.05);
}


/* ============================================================
   COPY
============================================================ */

.platform-content {
  min-width: 0;
}


.platform-title-row {
  display: flex;
  align-items: center;

  gap: 8px;
}


.platform-title {
  color: #383d45;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  line-height: 1.3;
}


.platform-description {
  margin-top: 4px;

  color: #999ea6;

  font-size: 8px;

  line-height: 1.4;
}


/* ============================================================
   STATUS
============================================================ */

.status-badge {
  display: inline-flex;
  align-items: center;

  gap: 5px;

  min-height: 21px;

  padding:
    0
    7px;

  color: #9a9ea5;

  background:
    rgba(
      30,
      40,
      55,
      0.04
    );

  border:
    1px solid
    rgba(
      30,
      40,
      55,
      0.055
    );

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;
}


.status-dot {
  width: 4px;
  height: 4px;

  background: #b2b5ba;

  border-radius: 50%;
}


.status-badge.active {
  color: #278457;

  background:
    rgba(
      52,
      181,
      112,
      0.075
    );

  border-color:
    rgba(
      52,
      181,
      112,
      0.11
    );
}


.status-badge.active
.status-dot {
  background: #39b874;
}


/* ============================================================
   INPUT
============================================================ */

.input-shell {
  width: 100%;
  min-height: 43px;

  display: flex;
  align-items: center;

  box-sizing: border-box;

  margin-top: 10px;

  padding:
    0
    9px
    0
    13px;

  background: #f7f8fa;

  border:
    1px solid
    #e3e6ea;

  border-radius: 12px;

  transition:
    background-color
    0.2s ease,
    border-color
    0.2s ease,
    box-shadow
    0.2s ease;
}


.social-row.focused
.input-shell {
  background: #ffffff;

  border-color: #5c8ff7;

  box-shadow:
    0
    0
    0
    3px
    rgba(
      92,
      143,
      247,
      0.1
    );
}


.input-shell input {
  min-width: 0;

  flex: 1;

  padding: 0;

  color: #30353d;

  background: transparent;

  border: none;
  outline: none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
}


.input-shell input::placeholder {
  color: #aaafb7;
}


/* ============================================================
   CONNECTED
============================================================ */

.connected-mark {
  width: 27px;
  height: 27px;

  flex:
    0
    0
    27px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #3ba46d;

  background:
    rgba(
      56,
      175,
      111,
      0.075
    );

  border-radius: 8px;
}


.connected-mark svg {
  width: 14px;
  height: 14px;
}


/* ============================================================
   SAVE
============================================================ */

.save-area {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;

  margin-top: 12px;

  padding:
    18px
    8px
    2px;

  border-top:
    1px solid
    rgba(
      25,
      40,
      60,
      0.065
    );
}


.save-state {
  display: inline-flex;
  align-items: center;

  gap: 6px;

  color: #9a9fa7;

  font-size: 8px;
}


.save-state-dot {
  width: 5px;
  height: 5px;

  background: #b9bdc3;

  border-radius: 50%;
}


.save-state-dot.changed {
  background: #f2a23a;
}


/* ============================================================
   BUTTON
============================================================ */

.save-btn {
  min-width: 128px;
  min-height: 40px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding:
    0
    16px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3187ee,
      #4968e6
    );

  border: none;
  border-radius: 12px;

  box-shadow:
    0
    7px
    17px
    rgba(
      40,
      111,
      225,
      0.2
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  cursor: pointer;

  opacity: 0.4;

  transition:
    opacity
    0.2s ease,
    transform
    0.2s ease,
    box-shadow
    0.2s ease;
}


.save-btn.active {
  opacity: 1;
}


.save-btn:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0
    10px
    22px
    rgba(
      40,
      111,
      225,
      0.27
    );
}


.save-btn:disabled {
  cursor: default;

  box-shadow: none;
}


.button-spinner {
  width: 14px;
  height: 14px;

  border:
    2px solid
    rgba(
      255,
      255,
      255,
      0.35
    );

  border-top-color: #ffffff;

  border-radius: 50%;

  animation:
    buttonSpin
    0.65s
    linear
    infinite;
}


@keyframes buttonSpin {

  to {
    transform:
      rotate(360deg);
  }

}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.social-card {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0
    18px
    50px
    rgba(
      0,
      0,
      0,
      0.24
    );
}


html[data-theme="dark"]
.card-title,
html[data-theme="dark"]
.section-heading h3,
html[data-theme="dark"]
.platform-title {
  color: #f0f1f3;
}


html[data-theme="dark"]
.card-description,
html[data-theme="dark"]
.section-heading p,
html[data-theme="dark"]
.platform-description {
  color: #8d929b;
}


html[data-theme="dark"]
.social-row + .social-row {
  border-color:
    rgba(
      255,
      255,
      255,
      0.06
    );
}


html[data-theme="dark"]
.social-row:hover,
html[data-theme="dark"]
.social-row.focused {
  background:
    rgba(
      255,
      255,
      255,
      0.03
    );
}


html[data-theme="dark"]
.platform-icon {
  background:
    rgba(
      255,
      255,
      255,
      0.045
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.input-shell {
  background: #292b30;

  border-color: #3b3e45;
}


html[data-theme="dark"]
.social-row.focused
.input-shell {
  background: #2e3036;

  border-color: #588bec;

  box-shadow:
    0
    0
    0
    3px
    rgba(
      88,
      139,
      236,
      0.13
    );
}


html[data-theme="dark"]
.input-shell input {
  color: #f0f1f3;
}


html[data-theme="dark"]
.input-shell input::placeholder {
  color: #727780;
}


html[data-theme="dark"]
.save-area {
  border-color:
    rgba(
      255,
      255,
      255,
      0.065
    );
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 700px) {

  .social-page {
    gap: 13px;
  }


  .social-header-card {
    padding: 20px;

    border-radius: 20px;
  }


  .links-card {
    padding:
      21px
      12px
      14px;

    border-radius: 20px;
  }


  .social-row {
    grid-template-columns:
      40px
      minmax(0, 1fr);

    gap: 10px;

    padding:
      14px
      8px;
  }


  .platform-icon {
    width: 40px;
    height: 40px;
  }


  .status-badge {
    display: none;
  }


  .save-area {
    align-items: stretch;
    flex-direction: column;

    gap: 12px;
  }


  .save-btn {
    width: 100%;

    min-height: 42px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .social-row,
  .platform-icon img,
  .input-shell,
  .save-btn {
    transition: none;
  }


  .button-spinner {
    animation: none;
  }

}

</style>